import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCashbook, getStores } from '../api/api';
import { BookOpen, Calendar, Building2, TrendingUp, TrendingDown, Download } from 'lucide-react';
import PrivacyBlur, { PrivacyNoticeBanner } from '../components/PrivacyBlur';
import { shouldBlurData, isCashierRole } from '../utils/privacyUtils';

const Cashbook = () => {
  const { user } = useAuth();
  const [cashbookData, setCashbookData] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    store_id: '',
    start_date: '',
    end_date: ''
  });

  const isAdminOrSupervisor = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'store_manager' || user?.role === 'manager' || user?.role === 'admin_assistant';

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    // Auto-cargar para cajeros o cuando se selecciona tienda
    if (user?.role === 'cashier' || user?.role === 'store_manager' || filters.store_id) {
      loadCashbook();
    }
  }, [filters, user]);

  const loadStores = async () => {
    try {
      const data = await getStores();
      setStores(data);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadCashbook = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) {
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59);
        params.end_date = endDate.toISOString();
      }
      if (filters.store_id) params.store_id = filters.store_id;
      
      const data = await getCashbook(params);
      setCashbookData(data);
    } catch (error) {
      console.error('Error loading cashbook:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
  };

  // Helper function to escape CSV values properly
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If the value contains comma, newline, or double quote, wrap it in double quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"') || stringValue.includes(';')) {
      // Escape any existing double quotes by doubling them
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const exportToCSV = () => {
    if (!cashbookData?.transactions) return;
    
    const currency = cashbookData.currency || 'Q';
    const headers = ['Fecha', 'Descripción', 'Ingreso', 'Gasto', 'Saldo'];
    const rows = cashbookData.transactions.map(tx => [
      escapeCSVValue(formatDateShort(tx.date)),
      escapeCSVValue(tx.description || ''),
      tx.income ? `${currency}${tx.income.toFixed(2)}` : '',
      tx.expense ? `${currency}${tx.expense.toFixed(2)}` : '',
      `${currency}${tx.balance.toFixed(2)}`
    ]);
    
    // Use semicolon as separator for better Excel compatibility (especially in Spanish locale)
    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    // Add BOM for Excel to recognize UTF-8 encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `libro_caja_${cashbookData.store_name}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const currency = cashbookData?.currency || 'Q';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <BookOpen className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Libro de Caja
                </h1>
                <p className="text-gray-500 text-sm">
                  Estado de cuenta con saldo acumulado (estilo Excel)
                </p>
              </div>
            </div>
            {cashbookData?.transactions?.length > 0 && (
              <button
                onClick={exportToCSV}
                className="flex items-center space-x-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
              >
                <Download size={18} />
                <span>Exportar CSV</span>
              </button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Calendar className="text-indigo-600" size={20} />
            <span>Filtros</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {isAdminOrSupervisor && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                  <Building2 size={16} className="text-indigo-600" />
                  <span>Tienda</span>
                </label>
                <select
                  value={filters.store_id}
                  onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleccione una tienda</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Inicio
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Fin
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ start_date: '', end_date: '', store_id: '' })}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Privacy Notice Banner for Cashiers */}
        <PrivacyNoticeBanner show={isCashierRole(user)} />

        {/* Mensaje para seleccionar tienda */}
        {isAdminOrSupervisor && !filters.store_id && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <Building2 size={48} className="text-amber-500 mx-auto mb-3" />
            <p className="text-amber-700 font-medium">
              Seleccione una tienda para ver el libro de caja
            </p>
          </div>
        )}

        {/* Resumen - BORROSO para cajeros (datos agregados/históricos) */}
        {cashbookData && !cashbookData.error && (
          <PrivacyBlur isBlurred={isCashierRole(user)}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500">
                <p className="text-sm text-gray-500">Saldo Inicial</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {currency}{cashbookData.initial_balance?.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-emerald-500">
                <p className="text-sm text-gray-500 flex items-center space-x-1">
                  <TrendingUp size={14} />
                  <span>Total Ingresos</span>
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  {currency}{cashbookData.total_income?.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-red-500">
                <p className="text-sm text-gray-500 flex items-center space-x-1">
                  <TrendingDown size={14} />
                  <span>Total Gastos</span>
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {currency}{cashbookData.total_expense?.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-purple-500">
                <p className="text-sm text-gray-500">Saldo Final</p>
                <p className={`text-2xl font-bold ${cashbookData.final_balance >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                  {currency}{cashbookData.final_balance?.toFixed(2)}
                </p>
              </div>
            </div>
          </PrivacyBlur>
        )}

        {/* Tabla Libro de Caja */}
        {cashbookData && !cashbookData.error && (
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <BookOpen size={24} />
                <span>Libro de Caja - {cashbookData.store_name}</span>
              </h2>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-gray-500 mt-4">Cargando libro de caja...</p>
              </div>
            ) : cashbookData.transactions?.length === 0 ? (
              <div className="p-12 text-center">
                <BookOpen size={48} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay transacciones en el período seleccionado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide">
                        Fecha
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wide">
                        Descripción
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-emerald-700 uppercase tracking-wide bg-emerald-50">
                        Ingreso
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-red-700 uppercase tracking-wide bg-red-50">
                        Gasto
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-indigo-700 uppercase tracking-wide bg-indigo-50">
                        Saldo
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {cashbookData.transactions.map((tx, index) => {
                      const isBlurred = shouldBlurData(user, tx.date);
                      return (
                      <tr 
                        key={tx.id || index} 
                        className={`hover:bg-gray-50 transition-colors ${
                          tx.type === 'initial' ? 'bg-indigo-50 font-semibold' : ''
                        } ${isBlurred ? 'opacity-75' : ''}`}
                      >
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                          <PrivacyBlur isBlurred={isBlurred}>
                            {tx.type === 'initial' ? formatDateShort(tx.date) : formatDate(tx.date)}
                          </PrivacyBlur>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-800">
                          <PrivacyBlur isBlurred={isBlurred}>
                            <div>
                              <span className={tx.type === 'initial' ? 'font-bold text-indigo-700' : ''}>
                                {tx.description}
                              </span>
                              {tx.payment_method && tx.type === 'income' && (
                                <span className="ml-2 text-xs text-gray-400">
                                  ({tx.payment_method})
                                </span>
                              )}
                              {tx.category && tx.type === 'expense' && (
                                <span className="ml-2 text-xs text-gray-400">
                                  ({tx.category})
                                </span>
                              )}
                            </div>
                            {tx.seller_name && (
                              <div className="text-xs text-gray-400">Vendedor: {tx.seller_name}</div>
                            )}
                            {tx.cashier_name && (
                              <div className="text-xs text-gray-400">Cajero: {tx.cashier_name}</div>
                            )}
                          </PrivacyBlur>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right bg-emerald-50/50">
                          <PrivacyBlur isBlurred={isBlurred}>
                            {tx.income ? (
                              <span className="text-emerald-600 font-semibold">
                                {currency}{tx.income.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </PrivacyBlur>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right bg-red-50/50">
                          <PrivacyBlur isBlurred={isBlurred}>
                            {tx.expense ? (
                              <span className="text-red-600 font-semibold">
                                {currency}{tx.expense.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </PrivacyBlur>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right bg-indigo-50/50">
                          <PrivacyBlur isBlurred={isBlurred}>
                            <span className={`font-bold ${tx.balance >= 0 ? 'text-indigo-700' : 'text-red-600'}`}>
                              {currency}{tx.balance.toFixed(2)}
                            </span>
                          </PrivacyBlur>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  {/* Footer con totales */}
                  <tfoot>
                    <tr className="bg-gray-800 text-white font-bold">
                      <td colSpan="2" className="px-6 py-4 text-right uppercase">
                        Totales
                      </td>
                      <td className="px-6 py-4 text-right text-emerald-400">
                        {currency}{cashbookData.total_income?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-right text-red-400">
                        {currency}{cashbookData.total_expense?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-right text-indigo-300">
                        {currency}{cashbookData.final_balance?.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Cashbook;
